import { IntlShape } from 'react-intl';

export const groupNameIntlKeys: Record<string, string> = {
  Custom: 'movement-filter-group-custom',
  Registration: 'movement-filter-group-registration',
  'Registration File': 'movement-filter-group-registration-file',
  'Registration Label': 'movement-filter-group-registration-label',
  Contract: 'movement-filter-group-contract',
  'Voucher Group': 'movement-filter-group-voucher-group',
  Voucher: 'movement-filter-group-voucher',
  'Voucher File': 'movement-filter-group-voucher-file',
  Folder: 'movement-filter-group-folder',
  'Versioned File': 'movement-filter-group-versioned-file',
  Question: 'movement-filter-group-question',
};

export const groupedTypeMap: Record<string, { id: number; label: string }[]> = {
  Custom: [{ id: 0, label: 'movement-filter-action-custom' }],
  Registration: [
    { id: 1, label: 'movement-filter-action-registration-read' },
    { id: 2, label: 'movement-filter-action-registration-create' },
    { id: 3, label: 'movement-filter-action-registration-update' },
    { id: 4, label: 'movement-filter-action-registration-delete' },
  ],
  'Registration File': [
    { id: 5, label: 'movement-filter-action-registration-file-read' },
    { id: 6, label: 'movement-filter-action-registration-file-create' },
    { id: 7, label: 'movement-filter-action-registration-file-update' },
    { id: 8, label: 'movement-filter-action-registration-file-delete' },
  ],
  'Registration Label': [
    { id: 9, label: 'movement-filter-action-registration-label-read' },
    { id: 10, label: 'movement-filter-action-registration-label-create' },
    { id: 11, label: 'movement-filter-action-registration-label-update' },
    { id: 12, label: 'movement-filter-action-registration-label-delete' },
  ],
  Contract: [
    { id: 13, label: 'movement-filter-action-contract-read' },
    { id: 14, label: 'movement-filter-action-contract-create' },
    { id: 15, label: 'movement-filter-action-contract-update' },
    { id: 16, label: 'movement-filter-action-contract-delete' },
    { id: 43, label: 'movement-filter-action-contract-restore' },
  ],
  'Voucher Group': [
    { id: 17, label: 'movement-filter-action-voucher-group-read' },
    { id: 18, label: 'movement-filter-action-voucher-group-create' },
    { id: 19, label: 'movement-filter-action-voucher-group-update' },
    { id: 20, label: 'movement-filter-action-voucher-group-delete' },
    { id: 45, label: 'movement-filter-action-voucher-group-restore' },
  ],
  Voucher: [
    { id: 21, label: 'movement-filter-action-voucher-read' },
    { id: 22, label: 'movement-filter-action-voucher-create' },
    { id: 23, label: 'movement-filter-action-voucher-update' },
    { id: 24, label: 'movement-filter-action-voucher-delete' },
    { id: 44, label: 'movement-filter-action-voucher-restore' },
  ],
  'Voucher File': [
    { id: 25, label: 'movement-filter-action-voucher-file-read' },
    { id: 26, label: 'movement-filter-action-voucher-file-create' },
    { id: 27, label: 'movement-filter-action-voucher-file-update' },
    { id: 28, label: 'movement-filter-action-voucher-file-delete' },
  ],
  Folder: [
    { id: 29, label: 'movement-filter-action-folder-read' },
    { id: 30, label: 'movement-filter-action-folder-create' },
    { id: 31, label: 'movement-filter-action-folder-update' },
    { id: 32, label: 'movement-filter-action-folder-delete' },
    { id: 33, label: 'movement-filter-action-folder-add-to-registration' },
    { id: 34, label: 'movement-filter-action-folder-remove-from-registration' },
  ],
  'Versioned File': [
    { id: 35, label: 'movement-filter-action-versioned-file-create' },
    { id: 36, label: 'movement-filter-action-versioned-file-update' },
    { id: 37, label: 'movement-filter-action-versioned-file-delete' },
    { id: 38, label: 'movement-filter-action-versioned-file-add-new-version' },
  ],
  Question: [{ id: 39, label: 'movement-filter-action-question-create' }],
};

export const getReadableType = (type: number, intl?: IntlShape): string => {
  for (const group of Object.values(groupedTypeMap)) {
    const found = group.find((item) => item.id === type);
    if (found)
      return intl ? intl.formatMessage({ id: found.label }) : found.label;
  }
  return intl
    ? intl.formatMessage({ id: 'movement-filter-action-unknown' }, { type })
    : `Unknown (${type})`;
};

// Tree checkbox utility functions
export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

/**
 * Get the state of a parent checkbox based on its children's selection
 */
export const getParentCheckboxState = (
  childIds: number[],
  selectedIds: number[]
): CheckboxState => {
  const selectedChildren = childIds.filter((id) => selectedIds.includes(id));

  if (selectedChildren.length === 0) {
    return 'unchecked';
  } else if (selectedChildren.length === childIds.length) {
    return 'checked';
  } else {
    return 'indeterminate';
  }
};

/**
 * Get the state of the global "Show all" checkbox
 */
export const getGlobalCheckboxState = (
  selectedIds: number[]
): CheckboxState => {
  const allIds = Object.values(groupedTypeMap)
    .flat()
    .map((item) => item.id);
  return getParentCheckboxState(allIds, selectedIds);
};

/**
 * Toggle a parent group - if any children are selected, deselect all; otherwise select all
 */
export const toggleParentGroup = (
  groupName: string,
  currentSelectedIds: number[]
): number[] => {
  const groupItems = groupedTypeMap[groupName];
  if (!groupItems) return currentSelectedIds;

  const groupIds = groupItems.map((item) => item.id);
  const hasAnySelected = groupIds.some((id) => currentSelectedIds.includes(id));

  if (hasAnySelected) {
    // Remove all group items
    return currentSelectedIds.filter((id) => !groupIds.includes(id));
  } else {
    // Add all group items
    return Array.from(new Set([...currentSelectedIds, ...groupIds]));
  }
};

/**
 * Toggle a single child item
 */
export const toggleChildItem = (
  itemId: number,
  currentSelectedIds: number[]
): number[] => {
  if (currentSelectedIds.includes(itemId)) {
    return currentSelectedIds.filter((id) => id !== itemId);
  } else {
    return [...currentSelectedIds, itemId];
  }
};

/**
 * Toggle all items (for global "Show all" checkbox)
 */
export const toggleAllItems = (currentSelectedIds: number[]): number[] => {
  const allIds = Object.values(groupedTypeMap)
    .flat()
    .map((item) => item.id);
  const hasAnySelected = currentSelectedIds.length > 0;

  return hasAnySelected ? [] : allIds;
};
